import { PillarScores } from './compute';
import { spearmanCorrelation, computeQuintiles } from './backtest';

export interface PillarWeights {
  profitability: number;
  growth: number;
  safety: number;
  shareholderReturns: number;
  valuation: number;
}

export interface VintageStockData {
  symbol: string;
  sector: string;
  pillarScores: PillarScores;
  valuationScore: number | null;
  qualityScore: number | null;
  forwardReturn: number | null;
}

export const DEFAULT_WEIGHTS: PillarWeights = {
  profitability: 1.0,
  growth: 1.75,
  safety: 0.75,
  shareholderReturns: 0.5,
  valuation: 1,
};

function computeWeightedScore(s: VintageStockData, w: PillarWeights): number | null {
  const pillars = [
    { score: s.pillarScores.profitability, weight: w.profitability },
    { score: s.pillarScores.growth, weight: w.growth },
    { score: s.pillarScores.safety, weight: w.safety },
    { score: s.pillarScores.shareholderReturns, weight: w.shareholderReturns },
  ];

  const validPillars = pillars.filter(p => p.score !== null && p.weight > 0);
  if (validPillars.length === 0) return null;

  const qualityWeightSum = validPillars.reduce((s, p) => s + p.weight, 0);
  const quality = validPillars.reduce((s, p) => s + p.score! * p.weight, 0) / qualityWeightSum;

  if (s.valuationScore === null || w.valuation === 0) return quality;

  // Combine quality and valuation
  const totalWeight = qualityWeightSum + w.valuation;
  return (quality * qualityWeightSum + s.valuationScore * w.valuation) / totalWeight;
}

export function recomputeBacktest(
  vintageStocks: Record<string, VintageStockData[]>,
  vintages: string[],
  weights: PillarWeights,
): {
  quintileReturns: Record<string, number[]>;
  avgQuintileReturns: number[];
  compositeICs: Record<string, number | null>;
  avgCompositeIC: number | null;
} {
  const quintileReturns: Record<string, number[]> = {};
  const compositeICs: Record<string, number | null> = {};

  for (const v of vintages) {
    const stocks = vintageStocks[v] ?? [];
    const withScores = stocks
      .map(s => ({
        score: computeWeightedScore(s, weights),
        ret: s.forwardReturn,
      }))
      .filter((d): d is { score: number; ret: number } =>
        d.score !== null && d.ret !== null);

    if (withScores.length >= 25) {
      quintileReturns[v] = computeQuintiles(withScores);
      compositeICs[v] = spearmanCorrelation(
        withScores.map(d => d.score),
        withScores.map(d => d.ret),
      );
    } else {
      quintileReturns[v] = [];
      compositeICs[v] = null;
    }
  }

  const qVintages = vintages.filter(v => quintileReturns[v].length === 5);
  const avgQuintileReturns = qVintages.length > 0
    ? [0, 1, 2, 3, 4].map(q => qVintages.reduce((s, v) => s + quintileReturns[v][q], 0) / qVintages.length)
    : [];

  const compVals = vintages.map(v => compositeICs[v]).filter((v): v is number => v !== null);
  const avgCompositeIC = compVals.length > 0 ? compVals.reduce((a, b) => a + b, 0) / compVals.length : null;

  return { quintileReturns, avgQuintileReturns, compositeICs, avgCompositeIC };
}
